import { createContext, memo, useContext, useEffect, useMemo } from 'react';
import { IntegrationsQueries, IntegrationTypes } from '@frontend/api-integrations';
import { LocationsTypes } from '@frontend/api-locations';
import { PersonAPI, PersonTypes } from '@frontend/api-person';
import { useRemoveAppLoader } from '@frontend/document';
import { useTranslation } from '@frontend/i18n';
import { useQuery } from '@frontend/react-query-helpers';
import { ContentLoader } from '@frontend/design-system';

type InsuranceReportContainerState = {
  locationData: LocationsTypes.Location;
  personData: PersonTypes.Person;
  integrationDetails: IntegrationTypes.WritebackSupportedHookResponse;
  isLoading: boolean;
  sourceId: string;
  personPMSId: string;
  locationId: string;
  handleSaveToPms?: () => void;
  handlePrint?: () => void;
  onApiFetchFailure?: () => void;
  personId: string;
};

export const InsuranceReportContainerContext = createContext<InsuranceReportContainerState>({
  locationData: {} as LocationsTypes.Location,
  personData: {} as PersonTypes.Person,
  integrationDetails: {
    isInsuranceDetailsWritebackSupported: false,
    isDocumentCenterWritebackSupported: false,
    PMSName: '',
    isLoading: false,
  },
  isLoading: false,
  sourceId: '',
  personPMSId: '',
  personId: '',
  locationId: '',
  handleSaveToPms: () => {},
  handlePrint: () => {},
  onApiFetchFailure: () => {},
});

type InsuranceReportContainerProviderProps = {
  children: React.ReactNode;
  locationId: string;
  personId: string;
  sourceId: string;
  personPMSId: string;
  locationData: LocationsTypes.Location;
  handleSaveToPms?: () => void;
  handlePrint?: () => void;
  onApiFetchFailure?: () => void;
};

export const InsuranceReportContainerProvider = memo(
  ({
    children,
    locationId,
    personId,
    sourceId,
    personPMSId,
    handlePrint,
    handleSaveToPms,
    onApiFetchFailure,
    locationData,
  }: InsuranceReportContainerProviderProps) => {
    const { t } = useTranslation('insuranceEligibilityReport');
    const { removeAppLoader } = useRemoveAppLoader({ removeOnMount: false });

    const { data: personData, isLoading: isPersonDataLoading } = useQuery({
      queryKey: [locationId, personId],
      queryFn: () => PersonAPI.getPersonExtended(personId),
      enabled: !personPMSId && !sourceId,
      onError: () => console.error('Failed to get person data'),
    });

    const {
      isDocumentCenterWritebackSupported,
      PMSName,
      isLoading: isSyncAppDataLoading,
      isInsuranceDetailsWritebackSupported,
    } = IntegrationsQueries.useIsWritebackSupported({
      locationId,
      sourceId: sourceId ? sourceId : personData?.SourceID ?? '',
    });

    const isLoadingData = isPersonDataLoading || isSyncAppDataLoading;

    const providerValue = useMemo<InsuranceReportContainerState>(() => {
      return {
        locationData: locationData || ({} as LocationsTypes.Location),
        personData: personData || ({} as PersonTypes.Person),
        integrationDetails: {
          isInsuranceDetailsWritebackSupported,
          isDocumentCenterWritebackSupported,
          PMSName,
          isLoading: isSyncAppDataLoading,
        },
        isLoading: isLoadingData,
        personPMSId: personPMSId || personData?.PMID || '',
        sourceId: sourceId || personData?.SourceID || '',
        locationId: locationId,
        personId: personId || personData?.PersonID || '',
        handlePrint,
        handleSaveToPms,
        onApiFetchFailure,
      };
    }, [
      locationData,
      personData,
      isInsuranceDetailsWritebackSupported,
      isDocumentCenterWritebackSupported,
      PMSName,
      isSyncAppDataLoading,
      isLoadingData,
      personPMSId,
      sourceId,
      locationId,
      personId,
      handlePrint,
      handleSaveToPms,
      onApiFetchFailure,
    ]);

    // Removing the app loader once data is loaded
    useEffect(() => {
      if (!isLoadingData) {
        // Removing the app loader once data is loaded
        removeAppLoader();
      }
    }, [isLoadingData]);

    return (
      <InsuranceReportContainerContext.Provider value={providerValue}>
        <ContentLoader show={isLoadingData} message={t('Loading Insurance Data')} />
        {!isLoadingData && children}
      </InsuranceReportContainerContext.Provider>
    );
  }
);

InsuranceReportContainerProvider.displayName = 'InsuranceReportContainerProvider';
InsuranceReportContainerContext.displayName = 'InsuranceReportContainerContext';

export const useInsuranceEligibilityReportContainerContext = () => useContext(InsuranceReportContainerContext);
